import {
  INTRANET_CATEGORY_PUBLICATION,
  INTRANET_IMAGE_ACCEPT,
  type AttachmentScanStatus,
  type IntranetCategory,
  type IntranetCategoryPublication,
} from '@gb-ticket/shared-types';
import { ArrowLeftIcon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { ApiRequestError } from '@/lib/api';
import { coverSrc } from '@/products/intranet/features/intranet/cover-src';
import { intranetPanelClassName } from '@/products/intranet/features/intranet/intranet-panel';
import {
  useDeleteIntranetCategoryImage,
  useUpdateIntranetCategory,
  useUploadIntranetCategoryImage,
} from '@/products/intranet/features/intranet/use-intranet';
import { useIntranetColleagues } from '@/products/intranet/features/intranet/use-intranet-directory';
import { INTRANET_ROOT } from '@/products/intranet/intranet-paths';

const TABS = [
  ['allgemein', 'Allgemein'],
  ['einstellungen', 'Einstellungen'],
  ['sichtbarkeit', 'Sichtbarkeit'],
  ['mitglieder', 'Mitglieder'],
] as const;

type TabId = (typeof TABS)[number][0];

const ALLOWED = new Set(['image/jpeg', 'image/png', 'image/webp']);

export function CategorySettingsPage({ category }: { category: IntranetCategory }) {
  const [params, setParams] = useSearchParams();
  const requested = params.get('reiter');
  const tab: TabId = TABS.some((entry) => entry[0] === requested)
    ? (requested as TabId)
    : 'allgemein';

  function selectTab(next: string) {
    const copy = new URLSearchParams(params);
    if (next === 'allgemein') {
      copy.delete('reiter');
    } else {
      copy.set('reiter', next);
    }
    setParams(copy, { replace: true });
  }

  return (
    <section className="space-y-6">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to={`${INTRANET_ROOT}?category=${encodeURIComponent(category.slug)}`}>
          <ArrowLeftIcon aria-hidden />
          Zurück zum Channel
        </Link>
      </Button>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{category.name}</h1>
        <p className="text-sm text-muted-foreground">Einstellungen des Channels</p>
      </div>
      <Tabs value={tab} onValueChange={selectTab}>
        <TabsList>
          {TABS.map(([id, label]) => (
            <TabsTrigger key={id} value={id}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="allgemein">
          {tab === 'allgemein' ? <GeneralTab key={category.id} category={category} /> : null}
        </TabsContent>
        <TabsContent value="einstellungen">
          {tab === 'einstellungen' ? <SettingsTab key={category.id} category={category} /> : null}
        </TabsContent>
        <TabsContent value="sichtbarkeit">
          {tab === 'sichtbarkeit' ? (
            <VisibilityTab key={`${category.id}:${String(category.open)}`} category={category} />
          ) : null}
        </TabsContent>
        <TabsContent value="mitglieder">
          {tab === 'mitglieder' ? <MembersTab key={category.id} category={category} /> : null}
        </TabsContent>
      </Tabs>
    </section>
  );
}

const SWITCHES = [
  ['commentsEnabled', 'Kommentare'],
  ['reactionsEnabled', 'Reaktionen'],
  ['pdfDownloadEnabled', 'PDF-Download'],
  ['mediaDownloadEnabled', 'Download von Bildern und Videos'],
  ['newsfeedEnabled', 'Anzeige im Newsfeed'],
  ['memberListEnabled', 'Anzeige der Mitgliederliste'],
] as const;

const PUBLICATION: readonly [IntranetCategoryPublication, string][] = [
  ['existing_authors', 'Bisherige Autoren'],
  ['channel_admins', 'Nur Channel-Admins'],
  ['members', 'Alle Mitglieder direkt'],
  ['members_approval', 'Alle Mitglieder nach Genehmigung'],
];

function SettingsTab({ category }: { category: IntranetCategory }) {
  const update = useUpdateIntranetCategory();
  const [draft, setDraft] = useState<Partial<Record<(typeof SWITCHES)[number][0], boolean>>>({});
  const [publication, setPublication] = useState<IntranetCategoryPublication | null>(null);
  const shownPublication = publication ?? category.publication ?? 'existing_authors';

  function value(key: (typeof SWITCHES)[number][0]): boolean {
    return draft[key] ?? category[key] !== false;
  }

  function cancel() {
    setDraft({});
    setPublication(null);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await update.mutateAsync({
        id: category.id,
        body: {
          commentsEnabled: value('commentsEnabled'),
          reactionsEnabled: value('reactionsEnabled'),
          pdfDownloadEnabled: value('pdfDownloadEnabled'),
          mediaDownloadEnabled: value('mediaDownloadEnabled'),
          newsfeedEnabled: value('newsfeedEnabled'),
          memberListEnabled: value('memberListEnabled'),
          publication: shownPublication,
        },
      });
      cancel();
      toast.success('Einstellungen gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <form
      className={`${intranetPanelClassName} space-y-4`}
      onSubmit={(event) => void onSubmit(event)}
    >
      <div className="space-y-2">
        <Label htmlFor="channel-publication">Veröffentlichungsregel</Label>
        <select
          id="channel-publication"
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40"
          value={shownPublication}
          onChange={(event) => {
            const next = event.target.value;
            if ((INTRANET_CATEGORY_PUBLICATION as readonly string[]).includes(next)) {
              setPublication(next as IntranetCategoryPublication);
            }
          }}
        >
          {PUBLICATION.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <p className="text-sm text-muted-foreground">
          Gilt für Nachrichten. Anleitungen, Downloads und Formulare bleiben beim globalen
          Schreibrecht.
        </p>
      </div>
      {SWITCHES.map(([key, label]) => (
        <div key={key} className="flex items-center justify-between gap-4">
          <Label htmlFor={`channel-${key}`}>{label}</Label>
          <Switch
            id={`channel-${key}`}
            checked={value(key)}
            onCheckedChange={(checked) =>
              setDraft((current) => ({ ...current, [key]: checked === true }))
            }
          />
        </div>
      ))}
      <div className="flex gap-2">
        <Button type="submit" disabled={update.isPending}>
          Speichern
        </Button>
        <Button type="button" variant="outline" onClick={cancel} disabled={update.isPending}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

function VisibilityTab({ category }: { category: IntranetCategory }) {
  const update = useUpdateIntranetCategory();
  const [open, setOpen] = useState<boolean | null>(null);
  const shown = open ?? category.open === true;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await update.mutateAsync({ id: category.id, body: { open: shown } });
      setOpen(null);
      toast.success('Sichtbarkeit gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <form
      className={`${intranetPanelClassName} space-y-4`}
      onSubmit={(event) => void onSubmit(event)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor="channel-offen">Offen</Label>
          <p className="text-sm text-muted-foreground">
            Geschlossen und ohne Leser-Rollen und ohne direkte Mitglieder sehen alle Intranet-Nutzer
            die Beiträge. Offen zeigt den Channel in der Liste. Beiträge sehen Unbeteiligte erst
            nach dem Beitritt.
          </p>
        </div>
        <Switch id="channel-offen" checked={shown} onCheckedChange={setOpen} aria-label="Offen" />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={update.isPending}>
          Speichern
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(null)}
          disabled={update.isPending}
        >
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

function GeneralTab({ category }: { category: IntranetCategory }) {
  const update = useUpdateIntranetCategory();
  const upload = useUploadIntranetCategoryImage();
  const removeImage = useDeleteIntranetCategoryImage();
  const [name, setName] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [managerRoleId, setManagerRoleId] = useState<string | null>(null);
  const [iconName, setIconName] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [clearImage, setClearImage] = useState(false);
  const [clearBanner, setClearBanner] = useState(false);

  const shownName = name ?? category.name;
  const shownDescription = description ?? category.description ?? '';
  const shownRole = managerRoleId ?? category.managerRoleId ?? '';
  const shownIcon = iconName ?? category.iconName ?? '';
  const shownOrder = sortOrder ?? String(category.sortOrder);
  const roles = category.managerRoleOptions ?? [];
  const saving = update.isPending || upload.isPending || removeImage.isPending;

  function pick(kind: 'image' | 'banner', file: File | null) {
    const setPreview = kind === 'image' ? setImagePreview : setBannerPreview;
    const setFile = kind === 'image' ? setImageFile : setBannerFile;
    const setClear = kind === 'image' ? setClearImage : setClearBanner;
    setPreview((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous);
      }
      return file ? URL.createObjectURL(file) : null;
    });
    setFile(file);
    if (file) {
      setClear(false);
    }
  }

  function cancel() {
    setName(null);
    setDescription(null);
    setManagerRoleId(null);
    setIconName(null);
    setSortOrder(null);
    setClearImage(false);
    setClearBanner(false);
    pick('image', null);
    pick('banner', null);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = shownName.trim();
    if (trimmed.length < 2) {
      toast.error('Bitte einen Namen mit mindestens 2 Zeichen eingeben.');
      return;
    }
    const order = Number(shownOrder);
    if (!Number.isInteger(order) || order < 0 || order > 10_000) {
      toast.error('Die Reihenfolge ist eine ganze Zahl von 0 bis 10000.');
      return;
    }
    for (const file of [imageFile, bannerFile]) {
      if (file && (file.size <= 0 || !ALLOWED.has(file.type))) {
        toast.error('Erlaubt sind JPEG, PNG und WebP.');
        return;
      }
    }
    try {
      await update.mutateAsync({
        id: category.id,
        body: {
          name: trimmed,
          description: shownDescription.trim() === '' ? null : shownDescription.trim(),
          managerRoleId: shownRole === '' ? null : shownRole,
          iconName: shownIcon.trim() === '' ? null : shownIcon.trim(),
          sortOrder: order,
        },
      });
      if (imageFile) {
        await upload.mutateAsync({ id: category.id, kind: 'image', file: imageFile });
      } else if (clearImage && category.imageScanStatus) {
        await removeImage.mutateAsync({ id: category.id, kind: 'image' });
      }
      if (bannerFile) {
        await upload.mutateAsync({ id: category.id, kind: 'banner', file: bannerFile });
      } else if (clearBanner && category.bannerScanStatus) {
        await removeImage.mutateAsync({ id: category.id, kind: 'banner' });
      }
      cancel();
      toast.success('Allgemein gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <form
      className={`${intranetPanelClassName} space-y-4`}
      onSubmit={(event) => void onSubmit(event)}
    >
      <div className="space-y-2">
        <Label htmlFor="rubrik-name">Name</Label>
        <Input
          id="rubrik-name"
          value={shownName}
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="rubrik-beschreibung">Beschreibung</Label>
        <Textarea
          id="rubrik-beschreibung"
          value={shownDescription}
          maxLength={2000}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="rubrik-rolle">Verwaltende Rolle</Label>
        <select
          id="rubrik-rolle"
          className="flex h-11 w-full rounded-md border bg-background px-3 text-sm"
          value={shownRole}
          onChange={(event) => setManagerRoleId(event.target.value)}
        >
          <option value="">Keine</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="rubrik-icon">Icon</Label>
          <Input
            id="rubrik-icon"
            value={shownIcon}
            maxLength={40}
            placeholder="z. B. Newspaper"
            onChange={(event) => setIconName(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="rubrik-reihenfolge">Reihenfolge</Label>
          <Input
            id="rubrik-reihenfolge"
            inputMode="numeric"
            value={shownOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        </div>
      </div>
      <PictureField
        label="Channelbild"
        inputId="rubrik-bild"
        savedUrl={clearImage ? null : (category.imageUrl ?? null)}
        preview={imagePreview}
        scanStatus={category.imageScanStatus}
        onPick={(file) => pick('image', file)}
        onClear={() => {
          pick('image', null);
          setClearImage(true);
        }}
      />
      <PictureField
        label="Banner"
        inputId="rubrik-banner"
        savedUrl={clearBanner ? null : (category.bannerUrl ?? null)}
        preview={bannerPreview}
        scanStatus={category.bannerScanStatus}
        onPick={(file) => pick('banner', file)}
        onClear={() => {
          pick('banner', null);
          setClearBanner(true);
        }}
      />
      <div className="flex gap-2">
        <Button type="submit" disabled={saving}>
          Speichern
        </Button>
        <Button type="button" variant="outline" onClick={cancel} disabled={saving}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

function MembersTab({ category }: { category: IntranetCategory }) {
  const update = useUpdateIntranetCategory();
  const [roleIds, setRoleIds] = useState<string[] | null>(null);
  const [personIds, setPersonIds] = useState<string[] | null>(null);
  const [personQuery, setPersonQuery] = useState('');
  const colleagues = useIntranetColleagues({ q: personQuery || undefined, pageSize: 50 });
  const shownRoles = roleIds ?? category.readerRoleIds ?? [];
  const shownPeople = personIds ?? (category.directMembers ?? []).map((person) => person.id);
  const roles = category.managerRoleOptions ?? [];
  const knownNames = new Map<string, string>();
  for (const person of category.directMembers ?? []) {
    knownNames.set(person.id, person.displayName);
  }
  for (const person of colleagues.data?.items ?? []) {
    knownNames.set(person.id, person.displayName);
  }

  function toggleRole(roleId: string, checked: boolean) {
    setRoleIds(
      checked ? [...new Set([...shownRoles, roleId])] : shownRoles.filter((id) => id !== roleId),
    );
  }

  function togglePerson(personId: string, checked: boolean) {
    setPersonIds(
      checked
        ? [...new Set([...shownPeople, personId])]
        : shownPeople.filter((id) => id !== personId),
    );
  }

  function cancel() {
    setRoleIds(null);
    setPersonIds(null);
    setPersonQuery('');
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await update.mutateAsync({
        id: category.id,
        body: { readerRoleIds: shownRoles, memberUserIds: shownPeople },
      });
      cancel();
      toast.success('Mitglieder gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <form
      className={`${intranetPanelClassName} space-y-4`}
      onSubmit={(event) => void onSubmit(event)}
    >
      <p className="text-sm text-muted-foreground">
        Personen werden nur direkte Mitglieder. Eine Gruppe wird mit dem Channel verknüpft oder
        gelöst. Die Berechtigungsgruppe und ihre Benutzer bleiben.
      </p>
      <div className="space-y-2">
        <Label>Leser-Rollen</Label>
        <div className="max-h-48 space-y-2 overflow-y-auto">
          {roles.map((role) => (
            <label
              key={role.id}
              className="flex items-center gap-3 rounded-xl border border-border px-3 py-2 text-sm"
            >
              <Checkbox
                checked={shownRoles.includes(role.id)}
                onCheckedChange={(checked) => toggleRole(role.id, checked === true)}
              />
              <span className="font-medium">{role.name}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="channel-member-search">Direkte Personen</Label>
        {shownPeople.length > 0 ? (
          <ul className="flex flex-wrap gap-2 text-xs">
            {shownPeople.map((id) => (
              <li key={id}>
                <button
                  type="button"
                  className="rounded-md border px-2 py-1 hover:bg-muted"
                  onClick={() => togglePerson(id, false)}
                >
                  {knownNames.get(id) ?? id} ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <Input
          id="channel-member-search"
          value={personQuery}
          placeholder="Name suchen …"
          onChange={(event) => setPersonQuery(event.target.value)}
        />
        <div className="max-h-48 space-y-2 overflow-y-auto">
          {(colleagues.data?.items ?? []).map((person) => (
            <label
              key={person.id}
              className="flex items-center gap-3 rounded-xl border border-border px-3 py-2 text-sm"
            >
              <Checkbox
                checked={shownPeople.includes(person.id)}
                onCheckedChange={(checked) => togglePerson(person.id, checked === true)}
              />
              <span className="font-medium">{person.displayName}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={update.isPending}>
          Speichern
        </Button>
        <Button type="button" variant="outline" onClick={cancel} disabled={update.isPending}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

function PictureField({
  label,
  inputId,
  savedUrl,
  preview,
  scanStatus,
  onPick,
  onClear,
}: {
  label: string;
  inputId: string;
  savedUrl: string | null;
  preview: string | null;
  scanStatus: AttachmentScanStatus | null | undefined;
  onPick: (file: File | null) => void;
  onClear: () => void;
}) {
  const src = preview ?? (savedUrl ? coverSrc({ coverUrl: savedUrl }) : null);
  const waiting = !preview && !savedUrl && (scanStatus === 'PENDING' || scanStatus === 'BLOCKED');
  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>{label}</Label>
      {src ? <img src={src} alt="" className="max-h-40 rounded-lg border object-cover" /> : null}
      {waiting ? (
        <p className="text-sm text-muted-foreground">
          {scanStatus === 'BLOCKED'
            ? 'Das Bild wurde abgelehnt und bleibt unsichtbar.'
            : 'Das Bild wird geprüft und bleibt bis dahin unsichtbar.'}
        </p>
      ) : null}
      <Input
        id={inputId}
        type="file"
        accept={INTRANET_IMAGE_ACCEPT}
        onChange={(event) => onPick(event.target.files?.[0] ?? null)}
      />
      <Button type="button" variant="ghost" size="sm" onClick={onClear}>
        Bild entfernen
      </Button>
    </div>
  );
}
